import { LazyQueryExecFunction, OperationVariables, useLazyQuery } from '@apollo/client'
import IconDownload from 'assets/images/download.svg'
import IconReload from 'assets/images/reset-bold.svg'
import IconDelete from 'assets/images/icon-delete.svg'
import IconDot from 'assets/images/icon-dot.svg'
import IconInformation from 'assets/images/icon-information.svg'
import IconStart from 'assets/images/icon-start.svg'
import { useOutsideClick } from 'components/hooks/useOutsideClick'
import Loading from 'components/loading'
import SingleRowActionBase from 'components/partials/singleRowActionBase'
import { notify } from 'components/toastify'
import { MESSAGES, STATUS } from 'constants/messages'
import moment from 'moment'
import axiosClient from 'network/axiosClient'
import { IFile } from 'pages/bond-data-management/interface'
import { DELETE_FILE_BY_ID } from 'queries/adminPortal'
import React, { useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import ReactLoading from 'react-loading'
import Moment from 'react-moment'
import { headerFileTable } from './headers'
import { BOND_DATA_MANAGEMENT } from 'constants/common'

interface Props {
  setSelectTab: (tabIndex: number) => void
  setData: (data: any) => void
  getFileByPagination: LazyQueryExecFunction<any, OperationVariables>
  getFileByPaginationLoading: boolean
  listFile: IFile[]
  handleGetData: (take: number, skip: number) => Promise<void>
}

const FileTable: React.FC<Props> = ({
  setSelectTab,
  setData,
  getFileByPaginationLoading,
  listFile,
  handleGetData,
}) => {
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const [currentItemSelected, setCurrentItemSelected] = useState<IFile>()
  const [isShowAction, setIsShowAction] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)

  const ref = useOutsideClick(() => {
    setIsShowAction(false)
  })

  const [deleteFileById] = useLazyQuery(DELETE_FILE_BY_ID)

  const fetchMoreFile = () => {}

  const onHandleClickShowAction = (item: IFile) => {
    if (currentItemSelected?.fileId === item.fileId) {
      setIsShowAction(!isShowAction)
    } else {
      setIsShowAction(true)
    }
    setCurrentItemSelected(item)
  }

  const onClickDetailAction = async () => {
    if (currentItemSelected && currentItemSelected.fileId) {
      setData(currentItemSelected)
      setSelectTab(2)
    }
  }

  const onClickStartImportAction = async () => {
    setIsLoading(true)
    if (currentItemSelected && currentItemSelected.fileId) {
      const response = await axiosClient.post('/bondData/process', {
        fileId: currentItemSelected.fileId,
      })

      if (response.status === 200) {
        notify('Start the process, please take a look status on slack.', STATUS.SUCCESS)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    }

    setIsLoading(false)
  }

  const onClickDownloadAction = async () => {
    if (currentItemSelected && currentItemSelected.fileId) {
      const response = await axiosClient.get('/bondData/download', {
        params: { fileId: currentItemSelected.fileId },
      })

      if (response.status !== 200) {
        notify(MESSAGES.ERROR.E_SOMETHING_WENT_WRONG, STATUS.ERROR)
        return
      }

      const data = response.data

      if (data && data.url) {
        const downloadLink = document.createElement('a')
        downloadLink.href = data.url
        // downloadLink.download = 'filename_to_save_as.ext' // Specify the filename
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      }
    } else {
      notify(MESSAGES.ERROR.E_SOMETHING_WENT_WRONG, STATUS.ERROR)
      return
    }
  }

  const onClickDeleteAction = async () => {
    setIsLoading(true)
    if (currentItemSelected && currentItemSelected.fileId) {
      const response = await deleteFileById({ variables: { fileId: currentItemSelected.fileId } })
      if (response.data.deleteFileById) {
        notify('Delete file successfully', STATUS.SUCCESS)
      } else {
        notify(MESSAGES.ERROR.E_SOMETHING_WENT_WRONG, STATUS.ERROR)
      }
    }
    handleGetData(20, 0)
    setIsLoading(false)
    return
  }

  const classNameForStatus = (status: string) => {
    let className = ''

    switch (status) {
      case BOND_DATA_MANAGEMENT.FILE_STATUS.PROCESSED:
        className = 'text-green-500'
        break
      case BOND_DATA_MANAGEMENT.FILE_STATUS.FAILED:
        className = 'text-red-500'
        break
      case BOND_DATA_MANAGEMENT.FILE_STATUS.PROCESSING:
        className = 'text-yellow-500'
        break
      default:
        break
    }

    return className
  }

  return isLoading ? (
    <div className="flex justify-center p-5">
      <ReactLoading type="spin" color="#A989D8" height={30} width={30} />
    </div>
  ) : (
    <div>
      <div className="flex justify-end">
        <button title={'Reload data'} onClick={() => handleGetData(20, 0)}>
          <img src={IconReload} alt="iconReload" width={30} height={30} />
        </button>
      </div>
      <table className="w-full text-left border-2 border-neutral-6 rounded-t-lg overflow-hidden">
        <thead className="bg-primary-shade4 text-body1 text-neutral-2 md:pr-4">
          <tr>
            {headerFileTable.map((item: any, idx) => (
              <th key={idx} className={item.headerStyles}>
                <div className={`flex`}>
                  <span className={`${item.isOneLineRes && 'lg:mt-[-3.5px]'}`}>{item.name}</span>
                  <div className={`${item.isOneLineRes && 'lg:mt-[-3.5px]'}`}></div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
      </table>
      {getFileByPaginationLoading && <Loading className="relative py-6" height={30} width={30} />}

      {listFile?.length === 0 && !getFileByPaginationLoading ? (
        <p className="py-6 text-center text-body1 text-neutral-4">No data available.</p>
      ) : (
        Boolean(listFile !== null && listFile?.length > 0 && !getFileByPaginationLoading) && (
          <InfiniteScroll
            dataLength={listFile?.length}
            next={fetchMoreFile}
            hasMore={hasMoreData}
            height={'max-content'}
            style={{ maxHeight: 500, minHeight: 300 }}
            loader={''}
            endMessage={''}
            className="cus-scrollbar"
          >
            {listFile?.map((file: IFile, index: number) => (
              <table key={index} className="cursor-pointer w-full">
                <tbody>
                  <tr className="h-[72px] w-full hover:bg-neutral-7 pt-5">
                    <td className={` ${headerFileTable[0].rowWid}`}>
                      <span>{file.fileId}</span>
                    </td>
                    <td className={`${headerFileTable[1].rowWid}`}>
                      <span>{file.fileName}</span>
                    </td>
                    <td className={`${headerFileTable[2].rowWid}`}>
                      <span>{file.totalRow.toLocaleString()}</span>
                    </td>
                    <td className={`${headerFileTable[3].rowWid}`}>
                      <span>{file.totalSuccess.toLocaleString()}</span>
                    </td>
                    <td className={`${headerFileTable[4].rowWid}`}>
                      <span>{file.totalFailure.toLocaleString()}</span>
                    </td>
                    <td className={`${headerFileTable[5].rowWid}`}>
                      <span className={classNameForStatus(file.status)}>{file.status}</span>
                    </td>
                    <td className={`${headerFileTable[6].rowWid}`}>
                      <span>
                        {
                          <Moment format="yyyy-MM-DD HH:mm">
                            {moment.utc(file.createdAt ?? '')}
                          </Moment>
                        }
                      </span>
                    </td>
                    <td className={`${headerFileTable[7].rowWid}`}>
                      <button
                        className="min-w-[24px] mb-auto"
                        onClick={() => onHandleClickShowAction(file)}
                      >
                        <div className="relative">
                          <img src={IconDot} alt="Icon-Dot" className="" />
                          {currentItemSelected?.fileId === file.fileId && isShowAction && (
                            <div
                              className={`absolute right-[2px] h-auto p-3 border shadow-2xl w-[220px] border-neutral-7 bg-neutral-8 rounded-xl z-10`}
                              ref={ref}
                            >
                              <SingleRowActionBase
                                handleAction={onClickStartImportAction}
                                icon={IconStart}
                                block={
                                  currentItemSelected.status !==
                                  BOND_DATA_MANAGEMENT.FILE_STATUS.UPLOADED
                                }
                                action="StartImport"
                                txt="Start import"
                              />
                              <SingleRowActionBase
                                handleAction={onClickDetailAction}
                                icon={IconInformation}
                                action="Detail"
                                txt="Detail"
                              />
                              <SingleRowActionBase
                                handleAction={onClickDownloadAction}
                                icon={IconDownload}
                                action="Download"
                                txt="Download"
                              />
                              <SingleRowActionBase
                                handleAction={onClickDeleteAction}
                                block={![
                                  BOND_DATA_MANAGEMENT.FILE_STATUS.FAILED,
                                  BOND_DATA_MANAGEMENT.FILE_STATUS.UPLOADED,
                                ].includes(currentItemSelected.status)}
                                icon={IconDelete}
                                action="Delete"
                                txt="Delete"
                              />
                            </div>
                          )}
                        </div>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            ))}
          </InfiniteScroll>
        )
      )}
    </div>
  )
}

export default FileTable
